import { useState } from 'react';
import {
  ECaseRenewalStatus,
  IApplicationComments,
  IAssignedUsers,
  ICase,
  ICaseAttachmentResponse,
  ICaseDecision,
  ICaseFile,
  ICaseFormValues,
  ICaseHistory,
  ICaseLicence,
  ICaseOtherCommentFormValue,
  ICaseOtherComments,
  ICaseRenewalStatus,
  ICaseReprintLicence,
  ICaseThread,
  ICaseVariationStatus,
  ICaseWorker,
  ICommentFormValues,
  ICommentForThreadFormValues,
  IDocumentsFormValues,
  IEditTaskFormValue,
  IFileReview,
  INote,
  INoteFormValue,
  IReport,
  IReportFormValue,
  IReportResultFormValue,
  IReports,
  ITask,
  ITaskComments,
  ITaskFormValue,
  IThreadFormValues,
  IWeaponLicenceTemplates,
} from '../../../models/cases';
import { IPaginator } from '../../../models/paginator';
import { EPerPages, ISortOptions } from '../../../models/table';
import {
  addAttachmentsCase,
  assignCase,
  getLicencesForReprint,
  reprintLicence,
  createApplicationComment,
  createApplicationCommentForThread,
  getCaseThreads,
  createCaseThread,
  createCaseThreadComment,
  approveCase,
  closeCase,
  createCase,
  createCaseNote,
  createCaseTask,
  createCaseTaskComment,
  deleteAttachmentsCase,
  deleteCaseNote,
  deleteCaseTask,
  downloadAttachmentFile,
  downloadAllAttachmentFiles,
  getApplicationComments,
  getAssignedUsersCase,
  getAttachmentsData,
  getCase,
  getCaseDecision,
  getCaseNote,
  getCaseNotes,
  getCaseHistory,
  getCases,
  getCaseTask,
  getCaseTasks,
  getFileDownload,
  getFilePreview,
  getFileReview,
  getFilesData,
  getWeaponLicenceTemplates,
  getWorkers,
  refuseCase,
  rejectCase,
  updateCaseNote,
  updatePayment,
  verificationCase,
  getCaseReports,
  getCaseReport,
  createCaseReport,
  updateCaseReport,
  deleteCaseReport,
  updateApplicationCommentForThread,
  updateApplicationComment,
  finishVerificationCase,
  getCaseLicences,
  generateSummaryReport,
  getSummaryReportData,
  getProcessTypeOtherWorkers,
  createOtherCase,
  getCaseOtherComments,
  createCaseOtherComment,
  closeTask,
  downloadSummaryReportData,
  addnewTypeAttachmentsCase,
  getRenewalStatus,
  createCaseReportResult,
  getVariationStatus,
} from '../../../requests/case';
import { useAlert } from 'models/alertContext';
import { isSuccessResponse } from 'utils/apiResponse';
import { ActionTypes } from 'state/actions/alert';
import { useTranslations } from 'hooks/useTranslations';
import { EAlertVariants } from 'components/atoms/Alert';
import { useNavigate } from 'react-router-dom';
import { ECaseTaskNames } from 'constants/CaseTaskNames';

export const useCase = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const [cases, setCases] = useState<ICase[]>([]);
  const [caseFilesData, setCaseFilesData] = useState<ICaseFile[]>([]);
  const [caseAttachmentsData, setCaseAttachmentsData] = useState<ICaseAttachmentResponse[]>(
    [],
  );
  const [caseData, setCaseData] = useState<ICase | null>(null);
  const [caseRenewalStatus, setCaseRenewalStatus] = useState<ICaseRenewalStatus | null>(null);
  const [caseVariationStatus, setCaseVariationStatus] = useState<ICaseVariationStatus | null>(
    null,
  );
  const [caseDecision, setCaseDecision] = useState<ICaseDecision | null>(null);
  const [caseWorkersData, setCaseWorkersData] = useState<ICaseWorker[] | null>([]);
  const [caseReprintLicencesData, setCaseReprintLicencesData] = useState<
    ICaseReprintLicence[] | null
  >([]);
  const [caseOtherWorkersData, setCaseOtherWorkersData] = useState<ICaseWorker[] | null>([]);
  const [caseOtherCommentsData, setCaseOtherCommentsData] = useState<ICaseOtherComments[]>([]);
  const [paginatorOtherComments, setPaginatorOtherComments] = useState<IPaginator | null>(
    null,
  );
  const [reviewData, setReviewData] = useState<IFileReview | null>(null);
  const [applicationCommentsData, setApplicationCommentsData] = useState<
    IApplicationComments[] | []
  >([]);
  const [caseThreadsData, setCaseThreadsData] = useState<ICaseThread[] | []>([]);
  const [weaponLicenceTemplates, setWeaponLicenceTemplates] = useState<
    IWeaponLicenceTemplates[]
  >([]);
  const [paginator, setPaginator] = useState<IPaginator | null>(null);
  const [notes, setNotes] = useState<INote[]>([]);
  const [licences, setLicences] = useState<ICaseLicence[]>([]);
  const [note, setNote] = useState<INote | null>(null);
  const [paginatorNotes, setPaginatorNotes] = useState<IPaginator | null>(null);
  const [paginatorLicences, setPaginatorLicences] = useState<IPaginator | null>(null);
  const [history, setHistory] = useState<ICaseHistory[]>([]);
  const [paginatorHistory, setPaginatorHistory] = useState<IPaginator | null>(null);
  const [tasks, setTasks] = useState<ITask[]>([]);
  const [task, setTask] = useState<ITaskComments | null>(null);
  const [paginatorTasks, setPaginatorTasks] = useState<IPaginator | null>(null);
  const [reports, setReports] = useState<IReports[]>([]);
  const [report, setReport] = useState<IReport | null>(null);
  const [paginatorReports, setPaginatorReports] = useState<IPaginator | null>(null);
  const [assignedUsersCase, setAssignedUsersCase] = useState<IAssignedUsers[]>([]);
  const [paginatorAssignedUsers, setPaginatorAssignedUsers] = useState<IPaginator | null>(
    null,
  );

  const { setAlert } = useAlert();

  const getAssignedUsersCaseData = async (
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    body: Partial<any> = {},
    sort?: ISortOptions | null,
  ) => {
    const result = await getAssignedUsersCase(currentPage, perPage, body, sort);
    if (result) {
      const { content, ...paginatorData } = result;
      setAssignedUsersCase(content);
      setPaginatorAssignedUsers(paginatorData);
      return result;
    } else {
      setAssignedUsersCase([]);
      setPaginatorAssignedUsers(null);
      return null;
    }
  };
  const getCasesData = async (
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    body: Partial<any> = {},
    sort?: ISortOptions | null,
  ) => {
    const result = await getCases(currentPage, perPage, body, sort);
    if (result) {
      const { content, ...paginatorData } = result;
      setCases(content);
      setPaginator(paginatorData);
    } else {
      setCases([]);
      setPaginator(null);
    }
  };
  const getCaseData = async (id: string) => {
    const result = await getCase(id);
    if (result) {
      setCaseData(result);
    } else {
      setCaseData(null);
    }
    return result;
  };
  const getCaseRenewalStatus = async (id: string) => {
    const result = await getRenewalStatus(id);
    if (result) {
      setCaseRenewalStatus(result);
    } else {
      setCaseRenewalStatus(null);
    }
    return result;
  };
  const getCaseVariationStatus = async (id: string) => {
    const result = await getVariationStatus(id);
    if (result) {
      setCaseVariationStatus(result);
    } else {
      setCaseVariationStatus(null);
    }
    return result;
  };
  const getCaseDecisionData = async (id: string) => {
    try {
      const result = await getCaseDecision(id);
      setCaseDecision(result ?? null);
      return result;
    } catch (e: any) {
      setCaseDecision(null);
      if (e.response?.status !== 404) {
        setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: e.response?.data?.message || e.response?.data?.errors?.[0]?.message,
          variant: EAlertVariants.error,
        });
      }
      return null;
    }
  };
  const getWorkersData = async (id: string) => {
    const result = await getWorkers(id);
    setCaseWorkersData(result ?? []);
    return result;
  };

  const getReprintLicencesData = async (id: string, search?: string) => {
    const result = await getLicencesForReprint(id, search);
    setCaseReprintLicencesData(result ?? []);
    return result;
  };
  const getFileReviewData = async (fileId: string) => {
    const result = await getFileReview(fileId);
    setReviewData(result ?? null);
    return result;
  };

  const getCaseFilesData = async (id: string) => {
    try {
      const result = await getFilesData(id);
      if (result) {
        setCaseFilesData(result);
      } else {
        setCaseFilesData([]);
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response.data?.errors?.[0]?.message || e.response.data?.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };
  const getCaseAttachmentsData = async (id: string) => {
    try {
      const result = await getAttachmentsData(id);
      if (result) {
        setCaseAttachmentsData(result);
      } else {
        setCaseAttachmentsData([]);
      }
      return true;
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response.data?.errors?.[0]?.message || e.response.data?.message,
        variant: EAlertVariants.error,
      });
      return false;
    }
  };
  const previewCaseFile = async (id: string) => {
    try {
      return await getFilePreview(id);
    } catch (e: any) {
      console.error(e);
      return false;
    }
  };
  const downloadCaseFile = async (id: string) => {
    try {
      return await getFileDownload(id);
    } catch (e: any) {
      console.error(e);
      return false;
    }
  };
  const downloadCaseAttachmentFile = async (id: string) => {
    try {
      return await downloadAttachmentFile(id);
    } catch (e: any) {
      console.error(e);
      return false;
    }
  };
  const downloadCaseAttachmentFiles = async (id: string, typeId: string) => {
    try {
      return await downloadAllAttachmentFiles(id, typeId);
    } catch (e: any) {
      console.error(e);
      return false;
    }
  };

  const handleAddAttachments = async (body: IDocumentsFormValues) => {
    try {
      const response = await addAttachmentsCase({
        request: {
          caseUniqueId: body.caseUniqueId,
          documentRequirementUniqueId: body.documentRequirementUniqueId,
          fileNames: body.fileNames,
        },
        documents: body.documents,
      });

      if (response.status === 204) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.addAttachment.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const handleAddNewTypeAttachments = async (body: IDocumentsFormValues) => {
    try {
      const response = await addnewTypeAttachmentsCase(body.caseUniqueId, {
        request: {
          fileNames: body.fileNames,
          documentTypeUniqueId: body.documentTypeUniqueId,
        },
        documents: body.documents,
      });

      if (response.status === 204) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.addAttachment.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const deleteCaseAttachmentFile = async (id: string) => {
    try {
      const response = await deleteAttachmentsCase(id);
      if (response.status === 204) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.deleteAttachment.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const getApplicationCommentsData = async (fileId: string) => {
    const result = await getApplicationComments(fileId);
    setApplicationCommentsData(result ?? []);
    return result;
  };
  const addApplicationComment = async (id: string, body: ICommentFormValues) => {
    try {
      const response = await createApplicationComment(id, body);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.addComment.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const editApplicationThread = async (
    id: string,
    threadId: string,
    body: IThreadFormValues,
  ) => {
    try {
      const response = await updateApplicationComment(id, threadId, body);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.editThread.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const addApplicationCommentForThread = async (
    id: string,
    threadId: string,
    body: ICommentForThreadFormValues,
  ) => {
    try {
      const response = await createApplicationCommentForThread(id, threadId, body.comment);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.addComment.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const editApplicationCommentForThread = async (
    id: string,
    commentId: string,
    body: ICommentForThreadFormValues,
  ) => {
    try {
      const response = await updateApplicationCommentForThread(id, commentId, body.comment);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.editComment.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const getCaseThreadsData = async (id: string) => {
    const result = await getCaseThreads(id);
    setCaseThreadsData(result ?? []);
    return result;
  };

  const addCaseThread = async (id: string, body: ICommentFormValues) => {
    try {
      const response = await createCaseThread(id, body);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.addComment.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const addCaseThreadComment = async (
    id: string,
    threadId: string,
    body: ICommentForThreadFormValues,
  ) => {
    try {
      const response = await createCaseThreadComment(id, threadId, body.comment);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.addComment.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const getTemplates = async (id: string, query: string) => {
    const result = await getWeaponLicenceTemplates(id, query);
    if (result) {
      setWeaponLicenceTemplates(result);
      return result;
    }
    setWeaponLicenceTemplates([]);
    return [];
  };

  const handleCreateCase = async (body: ICaseFormValues) => {
    try {
      const { documents, documentTemplateUniqueIds, documentsFilenames } = Object.entries(
        body.documents ?? [],
      ).reduce(
        (acc, [key, files]) => {
          files.forEach((file, index) => {
            acc.documents.push(file);
            acc.documentTemplateUniqueIds.push(key);
            if (body.documentsFilenames) {
              acc.documentsFilenames.push(body.documentsFilenames[key][index]);
            }
          });
          return acc;
        },
        {
          documents: [] as File[],
          documentTemplateUniqueIds: [] as string[],
          documentsFilenames: [] as string[],
        },
      );

      const response = await createCase({
        request: {
          legalUniqueId: body.legalUniqueId,
          licenceAuthorityUniqueId: body.licenceAuthorityUniqueId,
          paymentStatusUniqueId: body.paymentStatusUniqueId,
          processTypeUniqueId: body.processTypeUniqueId,
          weaponLicenceTemplateUniqueId: body.licenceCodeName,
          documentTemplateUniqueIds: documentTemplateUniqueIds,
          documentsFilenames: documentsFilenames,
          applicationsFilenames: body.applicationsFilenames,
        },
        applicationFiles: body.applicationFiles,
        documents: documents ?? null,
      });
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('caseManagement.createCases.success.text'),
        });
        return response.data.uniqueId;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const handleCreateOtherCase = async (body: ICaseFormValues) => {
    try {
      const data = {
        legalUniqueId: body.legalUniqueId,
        licenceAuthorityUniqueId: body.licenceAuthorityUniqueId,
        taskNameUniqueId: body.taskNameUniqueId ?? '',
        workerUniqueId: body.workerUniqueId ?? '',
        description: body.description ?? '',
      };
      if (body.taskNameUniqueId === ECaseTaskNames.REPRINT_LICENCE) {
        (data as any).weaponLicenceUniqueId = body.weaponLicenceUniqueId;
      }
      const response = await createOtherCase(data);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('caseManagement.createCases.success.text'),
        });
        return response.data.uniqueId;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const handleVerifyCase = async (id: string) => {
    try {
      const result = await verificationCase(id);
      if (result.status === 200) {
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const handleFinishVerificationCase = async (id: string) => {
    try {
      const result = await finishVerificationCase(id);
      if (result.status === 200) {
        setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.cases.finishVerificationCaseSuccess.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const handleAddDecision = async (
    id: string,
    isApproved: boolean,
    body: { reasonUniqueIds: string[]; comment: string },
  ) => {
    try {
      const result = isApproved ? await approveCase(id, body) : await refuseCase(id, body);
      if (isSuccessResponse(result)) {
        setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.cases.addDecisionSuccess.text'),
          variant: EAlertVariants.success,
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text:
          e.response?.data?.message ||
          e.response?.data?.errors?.[0]?.message ||
          t('general.somethingWentWrong.message'),
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const handleRejectCase = async (
    id: string,
    body: { reasonUniqueIds: string[]; comment: string },
  ) => {
    try {
      const result = await rejectCase(id, body);
      if (isSuccessResponse(result)) {
        setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.cases.rejectCaseSuccess.text'),
          variant: EAlertVariants.success,
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text:
          e.response?.data?.message ||
          e.response?.data?.errors?.[0]?.message ||
          t('general.somethingWentWrong.message'),
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const handleCloseCase = async (id: string) => {
    try {
      const result = await closeCase(id);
      if (result.status === 200) {
        setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.cases.closeCaseSuccess.text'),
          variant: EAlertVariants.success,
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors?.[0]?.message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const handleCloseTask = async (id: string, summary: string) => {
    try {
      const result = await closeTask(id, summary);
      if (result.status === 204) {
        setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.cases.closeTaskSuccess.text'),
          variant: EAlertVariants.success,
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors?.[0]?.message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const handleUpdatePayment = async (id: string, paymentStatusUniqueId: string) => {
    try {
      await updatePayment(id, paymentStatusUniqueId);
      await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('weaponLicenceCase.details.paymentUpdate.success'),
      });
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const handleReprintLicence = async (id: string) => {
    try {
      const result = await reprintLicence(id);
      if (result.status === 201) {
        setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.cases.reprintLicence.success.message'),
          variant: EAlertVariants.success,
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const handleAssignCase = async (id: string, workerUniqueId: string) => {
    try {
      const result = await assignCase(id, workerUniqueId);
      if (result.status === 200) {
        setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.cases.assignSuccess.text'),
          variant: EAlertVariants.success,
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const getNotesData = async (
    id: string,
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    sort?: ISortOptions | null,
  ) => {
    const result = await getCaseNotes(id, currentPage, perPage, sort);
    if (result) {
      const { content, ...paginatorData } = result;
      setNotes(content);
      setPaginatorNotes(paginatorData);
    } else {
      setNotes([]);
      setPaginatorNotes(null);
    }
  };
  const getHistoryData = async (
    id: string,
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    sort?: ISortOptions | null,
  ) => {
    const result = await getCaseHistory(id, currentPage, perPage, sort);
    if (result) {
      const { content, ...paginatorData } = result;
      setHistory(content);
      setPaginatorHistory(paginatorData);
    } else {
      setHistory([]);
      setPaginatorHistory(null);
    }
  };
  const getLicencesData = async (
    id: string,
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    sort?: ISortOptions | null,
  ) => {
    const result = await getCaseLicences(id, currentPage, perPage, sort);
    if (result) {
      const { content, ...paginatorData } = result;
      setLicences(content);
      setPaginatorLicences(paginatorData);
    } else {
      setLicences([]);
      setPaginatorLicences(null);
    }
  };

  const getReportsData = async (
    id: string,
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    sort?: ISortOptions | null,
  ) => {
    const result = await getCaseReports(id, currentPage, perPage, sort);
    if (result) {
      const { content, ...paginatorData } = result;
      setReports(content);
      setPaginatorReports(paginatorData);
    } else {
      setReports([]);
      setPaginatorReports(null);
    }
  };
  const getTasksData = async (
    id: string,
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    sort?: ISortOptions | null,
  ) => {
    const result = await getCaseTasks(id, currentPage, perPage, sort);
    if (result) {
      const { content, ...paginatorData } = result;
      setTasks(content);
      setPaginatorTasks(paginatorData);
    } else {
      setTasks([]);
      setPaginatorTasks(null);
    }
  };
  const getNoteData = async (id: string) => {
    const result = await getCaseNote(id);
    setNote(result ?? null);
    return result;
  };
  const getReportData = async (id: string) => {
    const result = await getCaseReport(id);
    setReport(result ?? null);
    return result;
  };
  const getTaskData = async (id: string) => {
    const result = await getCaseTask(id);
    setTask(result ?? null);
    return result;
  };

  const addNote = async (id: string, body: INoteFormValue) => {
    try {
      const response = await createCaseNote(id, body);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('caseManagement.addNote.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const addReport = async (id: string, body: IReportFormValue) => {
    try {
      const response = await createCaseReport(id, body);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('caseManagement.addCaseReport.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const addReportResult = async (reportId: string, body: IReportResultFormValue) => {
    try {
      let response = null;
      if (body.trace && body.trace.toString() === 'false') {
        response = await createCaseReportResult(reportId, {
          request: {
            trace: body.trace,
          },
        });
      } else if (body.trace && body.trace.toString() === 'true') {
        response = await createCaseReportResult(reportId, {
          request: {
            trace: body.trace,
            conclusions: body.conclusions,
          },
          attachments: body.attachments,
        });
      }

      if (response && response.status === 204) {
        await setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('caseManagement.createReport.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const updateReport = async (reportId: string, body: IReportResultFormValue) => {
    try {
      let response = null;
      if (body.trace && body.trace.toString() === 'false') {
        response = await updateCaseReport(reportId, {
          request: {
            trace: body.trace,
            deletedAttachmentsUniqueIds: body.deletedAttachmentsUniqueIds ?? [],
          },
        });
      } else if (body.trace && body.trace.toString() === 'true') {
        response = await updateCaseReport(reportId, {
          request: {
            trace: body.trace,
            conclusions: body.conclusions,
            deletedAttachmentsUniqueIds: body.deletedAttachmentsUniqueIds ?? [],
          },
          attachments: body.attachments,
        });
      }

      if (response && response.status === 204) {
        await setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('caseManagement.updateReport.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const addTask = async (id: string, body: ITaskFormValue) => {
    try {
      const response = await createCaseTask(id, body);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('caseManagement.addTask.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const addTaskComment = async (id: string, body: IEditTaskFormValue) => {
    try {
      const response = await createCaseTaskComment(id, body);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('caseManagement.addTaskComment.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const updateNote = async (id: string, noteId: string, body: INoteFormValue) => {
    try {
      const response = await updateCaseNote(id, noteId, body);
      if (response.status === 204) {
        await setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
          text: t('caseManagement.updateNote.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const deleteNote = async (id: string, noteId: string) => {
    try {
      const response = await deleteCaseNote(id, noteId);
      if (response.status === 204) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.deleteNote.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const deleteTask = async (id: string) => {
    try {
      const response = await deleteCaseTask(id);
      if (response.status === 204) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.deleteTask.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const deleteReport = async (id: string, fromDetails: boolean = false) => {
    try {
      const response = await deleteCaseReport(id);
      if (response.status === 204) {
        await setAlert(
          fromDetails ? ActionTypes.SET_NEXT_PAGE_ALERT : ActionTypes.SET_SAME_PAGE_ALERT,
          {
            text: t('caseManagement.deleteReport.success.text'),
          },
        );
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  const handleGenerateSummaryReport = async (id: string) => {
    try {
      const response = await generateSummaryReport(id);
      if (response.status === 200) {
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };
  const getSummaryReport = async (id: string, isDownload: boolean = false) => {
    let result = null;
    if (isDownload) {
      result = await downloadSummaryReportData(id);
    } else {
      result = await getSummaryReportData(id);
    }
    if (result) {
      return result.url;
    }
    return false;
  };
  const getOtherWorkers = async (licenceAuthorityUniqueId: string) => {
    const result = await getProcessTypeOtherWorkers(licenceAuthorityUniqueId);
    if (result) {
      setCaseOtherWorkersData(result);
    } else {
      setCaseOtherWorkersData([]);
    }
    return false;
  };

  const getCaseOtherCommentsData = async (
    caseId: string,
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage100,
    sort?: ISortOptions | null,
  ) => {
    const result = await getCaseOtherComments(caseId, currentPage, perPage, sort);
    if (result) {
      const { content, ...paginatorData } = result;
      setCaseOtherCommentsData(content);
      setPaginatorOtherComments(paginatorData);
    } else {
      setCaseOtherCommentsData([]);
      setPaginatorOtherComments(null);
    }
  };
  const addCaseOtherComment = async (id: string, body: ICaseOtherCommentFormValue) => {
    try {
      const response = await createCaseOtherComment(id, body);
      if (response.status === 200) {
        await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('caseManagement.addComment.success.text'),
        });
        return true;
      }
    } catch (e: any) {
      setAlert(ActionTypes.SET_MODAL_ALERT, {
        text: e.response?.data?.message || e.response?.data?.errors[0].message,
        variant: EAlertVariants.error,
      });
    }
    return false;
  };

  return {
    cases,
    paginator,
    getCasesData,
    handleCreateCase,
    getTemplates,
    weaponLicenceTemplates,
    getCaseData,
    caseData,
    caseDecision,
    getCaseDecisionData,
    handleAddDecision,
    handleVerifyCase,
    handleCloseCase,
    handleRejectCase,
    getCaseFilesData,
    caseFilesData,
    previewCaseFile,
    downloadCaseFile,
    handleUpdatePayment,
    caseAttachmentsData,
    getCaseAttachmentsData,
    downloadCaseAttachmentFile,
    downloadCaseAttachmentFiles,
    handleAddAttachments,
    handleAddNewTypeAttachments,
    handleAssignCase,
    getWorkersData,
    caseWorkersData,
    handleReprintLicence,
    getReprintLicencesData,
    caseReprintLicencesData,
    getFileReviewData,
    reviewData,
    getApplicationCommentsData,
    applicationCommentsData,
    addApplicationComment,
    addApplicationCommentForThread,
    getCaseThreadsData,
    caseThreadsData,
    addCaseThread,
    addCaseThreadComment,
    deleteCaseAttachmentFile,
    getNotesData,
    notes,
    paginatorNotes,
    getHistoryData,
    history,
    paginatorHistory,
    getNoteData,
    note,
    addNote,
    deleteNote,
    updateNote,
    getTasksData,
    paginatorTasks,
    tasks,
    task,
    addTask,
    addTaskComment,
    getTaskData,
    deleteTask,
    getAssignedUsersCaseData,
    assignedUsersCase,
    paginatorAssignedUsers,
    getReportsData,
    paginatorReports,
    reports,
    getReportData,
    report,
    addReport,
    addReportResult,
    updateReport,
    deleteReport,
    editApplicationCommentForThread,
    editApplicationThread,
    handleFinishVerificationCase,
    licences,
    paginatorLicences,
    getLicencesData,
    handleGenerateSummaryReport,
    getSummaryReport,
    getOtherWorkers,
    caseOtherWorkersData,
    handleCreateOtherCase,
    getCaseOtherCommentsData,
    caseOtherCommentsData,
    paginatorOtherComments,
    addCaseOtherComment,
    handleCloseTask,
    getCaseRenewalStatus,
    caseRenewalStatus,
    getCaseVariationStatus,
    caseVariationStatus,
  };
};
